import { useLazyQuery } from '@apollo/client'
import { uniqBy } from 'lodash'
import { ReactElement, useState } from 'react'

import { SelectTags } from 'components/v2/SelectTags'
import { ISelectTagsProps } from 'components/v2/SelectTags/types'
import { useDebouncedQuery } from 'components/v2/Autocomplete'
import { GET_ROUTES_SUGGESTIONS } from 'features/Routes/queries-legacy'
import { GTRouteSuggestion, GTRouteSuggestionsResponse } from 'gql/types.generated'
import { getEntitiesWithSameKey } from 'features/Services/components/MultiRouteServiceAutocomplete/utils'

interface IMultiRouteAutocompleteProps extends Omit<ISelectTagsProps, 'onChange' | 'items' | 'getTagLabel'> {
  onChange: (routes: GTRouteSuggestion[]) => void
  uniqueName?: boolean
}

export const MultiRouteAutocomplete = ({
  onChange,
  uniqueName = false,
  label,
  placeholder,
  values,
  ...props
}: IMultiRouteAutocompleteProps): ReactElement => {
  const [pattern, setPattern] = useState('')
  const [getRoutes, { data, loading, error }] = useLazyQuery<{
    routeSuggestions: GTRouteSuggestionsResponse
  }>(GET_ROUTES_SUGGESTIONS)
  const { isDebouncing } = useDebouncedQuery({
    query: getRoutes,
    pattern
  })

  const filteredItems =
    data?.routeSuggestions?.items.filter((route) => !values.map(({ id }) => id).includes(route.id)) || []
  const shownItems = uniqueName ? uniqBy(filteredItems, 'name') : filteredItems

  const handleChange = (routes: GTRouteSuggestion[], pattern = ''): void => {
    setPattern(pattern)

    if (uniqueName) {
      const newRoutes = getEntitiesWithSameKey(routes, data?.routeSuggestions?.items || [], 'name')
      onChange(newRoutes)
    } else {
      onChange(routes)
    }
  }

  const shownValues = uniqueName ? uniqBy(values, 'name') : values

  return (
    <SelectTags
      placeholder={placeholder}
      values={shownValues}
      label={label}
      onChange={handleChange}
      items={shownItems}
      getTagLabel={(route: GTRouteSuggestion): string => route.name}
      loading={loading || isDebouncing}
      error={error}
      {...props}
    />
  )
}
